import React from "react";
import PropTypes from "prop-types";
import Link from "next/link";

import SquarefootFeaturedListings from "components/SquarefootFeaturedListings";
import ListingCard from "components/cards/ListingCard";
import NoOfEmployeesEngagementBox from "components/cards/NoOfEmployeesEngagementBox";

import { titleize } from "util/strings";

import styles from "./styles.module.scss";

const SearchResults = ({
  listings,
  count,
  page,
  limit,
  area,
  areaUuid,
  onHoverListing,
  city,
  neighborhoods,
  submarkets,
  market,
  loggedIn,
  children,
}) => {
  return (
    <section className="inset">
      <div className={styles.searchHeadingWrapper}>
        <h1 className={`subheaderEmphasis ${styles.header}`}>
          {titleize(area)} Office Space For Rent
        </h1>
        {children}
      </div>
      {page === 1 && (
        <SquarefootFeaturedListings
          city={city}
          neighborhoods={
            neighborhoods
              .map(({ parameterizedName }) => parameterizedName)
              .join(",") || ""
          }
          areaUuid={areaUuid}
        />
      )}
      <p className={styles.header}>
        Display {page * limit - limit + 1} - {Math.min(page * limit, count)} of{" "}
        {count} listings.
      </p>
      <ul className={styles.cards}>
        {listings.slice(0, 2).map((listing, index) => (
          <Link
            href={`/building${listing.building.slug}/${listing.building.identity}?listingId=${listing.id}`}
            key={listing.id}
            passHref
          >
            <ListingCard
              eager={index < 4}
              listing={listing}
              onMouseEnter={() => onHoverListing(listing)}
              onMouseLeave={() => onHoverListing(null)}
            />
          </Link>
        ))}
        {!loggedIn && (
          <NoOfEmployeesEngagementBox
            market={market?.id}
            city={city}
            areas={[...submarkets, ...neighborhoods]}
            lead="Public Search"
            source="External"
          />
        )}
        {listings.slice(2).map((listing, index) => (
          <Link
            href={`/building${listing.building.slug}/${listing.building.identity}?listingId=${listing.id}`}
            key={listing.id}
            passHref
          >
            <ListingCard
              eager={index < 4}
              listing={listing}
              onMouseEnter={() => onHoverListing(listing)}
              onMouseLeave={() => onHoverListing(null)}
            />
          </Link>
        ))}
      </ul>
    </section>
  );
};

SearchResults.propTypes = {
  listings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      squareFeet: PropTypes.number,
      floorAndSuite: PropTypes.string,
      estimatedMonthlyPrice: PropTypes.number,
      accurateMonthlyPrice: PropTypes.number,
      coverImage: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
      }),
      sublease: PropTypes.bool,
      leaseTerm: PropTypes.string,
      leaseType: PropTypes.string,
      description: PropTypes.string,
      estimatedPsf: PropTypes.number,
      accuratePsf: PropTypes.number,
      building: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        identity: PropTypes.string,
        lat: PropTypes.number,
        lng: PropTypes.number,
        address: PropTypes.string,
        slug: PropTypes.string,
        classification: PropTypes.string,
        market: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        submarket: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        neighborhood: PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          name: PropTypes.string,
          parameterizedName: PropTypes.string,
        }),
      }),
      brokerInsights: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
        })
      ),
    })
  ),
  count: PropTypes.number,
  page: PropTypes.number,
  limit: PropTypes.number,
  area: PropTypes.string,
  onHoverListing: PropTypes.func,
  city: PropTypes.string,
  children: PropTypes.node,
  areaUuid: PropTypes.string,
  market: PropTypes.shape({
    id: PropTypes.string,
  }),
  submarkets: PropTypes.arrayOf(
    PropTypes.shape({
      externalId: PropTypes.string,
    })
  ),
  neighborhoods: PropTypes.arrayOf(
    PropTypes.shape({
      externalId: PropTypes.string,
    })
  ),
  loggedIn: PropTypes.bool,
};

SearchResults.defaultProps = {
  listings: [],
  count: 0,
  page: 1,
  limit: 12,
  area: "",
  onHoverListing: () => {},
  neighborhoods: [],
  children: null,
  areaUuid: null,
  city: null,
  submarkets: [],
  market: null,
  loggedIn: false,
};

export default SearchResults;
